import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Bottone } from '../../componenti/base.tsx'
import { IconaArchivia, IconaBusta, IconaCestino, IconaRipristina } from '../../componenti/Icone.tsx'
import * as posta from '../../servizi/posta.ts'
import type { AzioneBlocco } from '../../servizi/posta-elenco.ts'
import { opzioniCartelle } from './CartellePersonali.tsx'
import '../../stile-posta-elenco.css'

/**
 * Selezione multipla nell'elenco (541; come in Roundcube): una casella di spunta per riga, «Seleziona tutte»
 * per le righe caricate, Maiusc+clic per un intervallo. Sulle selezionate la barra delle azioni: archivio,
 * cestino o ripristino, letta e da leggere, «Sposta in…», bandierina, etichetta. Le azioni valgono per le
 * conversazioni: nella vista per messaggi, per le conversazioni dei messaggi scelti.
 */

export type Selezione = {
  scelte: ReadonlySet<string>
  /** Spunta o toglie una riga; con `intervallo` (Maiusc) tutte quelle fra l'ultima toccata e questa. */
  alterna: (chiave: string, intervallo: boolean) => void
  tutte: (si: boolean) => void
  svuota: () => void
  tutteScelte: boolean
}

export function useSelezione(chiavi: string[]): Selezione {
  const [scelte, setScelte] = useState<ReadonlySet<string>>(new Set())
  const ancora = useRef<string | null>(null)
  const elenco = useRef(chiavi)
  elenco.current = chiavi

  // Le righe che escono dall'elenco (cartella cambiata, conversazione spostata) escono dalla selezione.
  useEffect(() => {
    setScelte((prima) => {
      if (!prima.size) return prima
      const viste = new Set(chiavi)
      const restano = [...prima].filter((k) => viste.has(k))
      return restano.length === prima.size ? prima : new Set(restano)
    })
  }, [chiavi])

  const alterna = useCallback((chiave: string, intervallo: boolean) => {
    // L'ultima riga toccata si legge adesso: l'aggiornamento qui sotto gira dopo, quando e' gia' questa.
    const precedente = ancora.current
    setScelte((prima) => {
      const nuove = new Set(prima)
      const tutte = elenco.current
      const da = precedente ? tutte.indexOf(precedente) : -1
      const a = tutte.indexOf(chiave)
      const accendi = !prima.has(chiave)
      if (intervallo && da >= 0 && a >= 0) {
        for (const k of tutte.slice(Math.min(da, a), Math.max(da, a) + 1)) {
          if (accendi) nuove.add(k)
          else nuove.delete(k)
        }
      } else if (accendi) nuove.add(chiave)
      else nuove.delete(chiave)
      return nuove
    })
    ancora.current = chiave
  }, [])

  const tutte = useCallback((si: boolean) => {
    setScelte(si ? new Set(elenco.current) : new Set())
    ancora.current = null
  }, [])
  const svuota = useCallback(() => { setScelte(new Set()); ancora.current = null }, [])

  return { scelte, alterna, tutte, svuota, tutteScelte: chiavi.length > 0 && chiavi.every((k) => scelte.has(k)) }
}

/** La casella di spunta della riga, fuori dal bottone della riga (un bottone non contiene controlli). */
export function SpuntaRiga({ scelta, etichetta, alterna }: {
  scelta: boolean; etichetta: string; alterna: (intervallo: boolean) => void
}) {
  return (
    <span className="posta-spunta-riga">
      <input type="checkbox" checked={scelta} aria-label={etichetta}
             // Il Maiusc si legge dal clic (anche la barra spaziatrice produce un clic): change non lo porta.
             onClick={(e) => alterna(e.shiftKey)} onChange={() => undefined} />
    </span>
  )
}

/** «Seleziona tutte» sopra l'elenco: spunta piena, vuota o a meta'. */
export function SelezionaTutte({ selezione, righe, children }: {
  selezione: Selezione; righe: number
  /** Accanto, a destra: l'ordine dell'elenco, «Segna tutte come lette». */
  children?: ReactNode
}) {
  const rif = useRef<HTMLInputElement>(null)
  const parziale = selezione.scelte.size > 0 && !selezione.tutteScelte
  useEffect(() => { if (rif.current) rif.current.indeterminate = parziale }, [parziale])
  if (!righe && !children) return null
  return (
    <div className="posta-seleziona-tutte">
      {righe > 0 && (
        <label>
          <input ref={rif} type="checkbox" checked={selezione.tutteScelte}
                 onChange={(e) => selezione.tutte(e.target.checked)} />
          Seleziona tutte
          {selezione.scelte.size > 0 && (
            <span className="tenue"> · {selezione.scelte.size === 1 ? '1 scelta' : `${selezione.scelte.size} scelte`}</span>
          )}
        </label>
      )}
      {children}
    </div>
  )
}

export function BarraSelezione({
  quante, stato, cartelle, cartellaAttuale, etichette, pieno, soloPersonali = false, inCorso, esegui, svuota,
}: {
  quante: number
  /** Lo stato dell'elenco: nel cestino si ripristina, fra le archiviate si riporta fra le aperte. */
  stato: posta.StatoThread
  /** Le cartelle di destinazione della casella; vuoto se non si sposta (vista unica, casella IMAP, sola lettura). */
  cartelle: posta.Cartella[]
  cartellaAttuale: string | null
  /** Le etichette che si possono mettere: le tue e quelle per tutti della casella. */
  etichette: posta.EtichettaPosta[]
  /** Accesso pieno alle caselle delle selezionate: bandierine ed etichette per tutti. */
  pieno: boolean
  /**
   * Casella letta in supervisione o con la sola lettura: lo stato, il letto e le cartelle non si cambiano (l'API
   * rifiuta), restano bandierina ed etichetta personali.
   */
  soloPersonali?: boolean
  inCorso: boolean
  esegui: (azione: AzioneBlocco) => void
  svuota: () => void
}) {
  const scegli = (valore: string, f: (v: string) => void) => { if (valore) f(valore) }
  const perTutti = pieno && !soloPersonali
  const opzioniEtichette = (pref: string) => etichette.filter((e) => perTutti || e.per === 'me')
    .map((e) => <option key={`${pref}${e.id}`} value={`${pref}${e.id}`}>{e.nome}{e.per === 'me' ? ' (solo per te)' : ''}</option>)
  return (
    <div className="posta-barra-selezione" role="toolbar" aria-label={`Azioni sulle ${quante} conversazioni scelte`}>
      <strong className="posta-selezione-conto">{quante === 1 ? '1 scelta' : `${quante} scelte`}</strong>
      {!soloPersonali && (stato === 'cestinato'
        ? <Bottone dimensione="piccolo" variante="discreto" icona={<IconaRipristina />} inCorso={inCorso}
                   onClick={() => esegui({ tipo: 'stato', stato: 'aperto' })}>Ripristina</Bottone>
        : (
          <>
            <Bottone dimensione="piccolo" variante="discreto" icona={<IconaArchivia />} inCorso={inCorso}
                     onClick={() => esegui({ tipo: 'stato', stato: stato === 'archiviato' ? 'aperto' : 'archiviato' })}>
              {stato === 'archiviato' ? 'Riporta fra le aperte' : 'Archivia'}
            </Bottone>
            <Bottone dimensione="piccolo" variante="discreto" icona={<IconaCestino />} inCorso={inCorso}
                     onClick={() => esegui({ tipo: 'stato', stato: 'cestinato' })}>Sposta nel cestino</Bottone>
          </>
        ))}
      {!soloPersonali && (
        <>
          <Bottone dimensione="piccolo" variante="discreto" icona={<IconaBusta />} inCorso={inCorso}
                   onClick={() => esegui({ tipo: 'letto', letto: true })}>Segna come lette</Bottone>
          <Bottone dimensione="piccolo" variante="discreto" inCorso={inCorso}
                   onClick={() => esegui({ tipo: 'letto', letto: false })}>Segna come da leggere</Bottone>
        </>
      )}
      {cartelle.length > 0 && !soloPersonali && (
        <select value="" aria-label="Sposta le scelte in una cartella" disabled={inCorso}
                onChange={(e) => scegli(e.target.value, (id) => esegui({ tipo: 'sposta', cartella_id: id }))}>
          <option value="">Sposta in…</option>
          {opzioniCartelle(cartelle.filter((c) => c.id !== cartellaAttuale))}
        </select>
      )}
      <select value="" aria-label="Bandierina sulle scelte" disabled={inCorso}
              onChange={(e) => scegli(e.target.value, (v) => {
                const [per, colore] = v.split(':') as [posta.Per, string]
                esegui({ tipo: 'bandierina', per, colore: colore === '-' ? null : colore as posta.Colore })
              })}>
        <option value="">Bandierina…</option>
        {(perTutti ? ['tutti', 'me'] as const : ['me'] as const).map((per) => (
          <optgroup key={per} label={per === 'tutti' ? 'Per tutti' : 'Solo per te'}>
            {posta.COLORI.map((c) => <option key={c.valore} value={`${per}:${c.valore}`}>Bandierina {c.nome}</option>)}
            <option value={`${per}:-`}>Togli la bandierina</option>
          </optgroup>
        ))}
      </select>
      {etichette.some((e) => perTutti || e.per === 'me') && (
        <select value="" aria-label="Etichetta sulle scelte" disabled={inCorso}
                onChange={(e) => scegli(e.target.value, (v) => {
                  const [cosa, id] = v.split(':') as ['metti' | 'togli', string]
                  esegui({ tipo: 'etichetta', etichetta_id: id, messa: cosa === 'metti' })
                })}>
          <option value="">Etichetta…</option>
          <optgroup label="Metti">{opzioniEtichette('metti:')}</optgroup>
          <optgroup label="Togli">{opzioniEtichette('togli:')}</optgroup>
        </select>
      )}
      <button type="button" className="posta-selezione-annulla" onClick={svuota}>Annulla la selezione</button>
    </div>
  )
}
